import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { PrimaryButton, SecondaryButton } from './Button'
import Checkbox from './Checkbox'
import Dropdown, { DropdownContext, DropdownItem } from './Dropdown'
import { InputLabel, TextInputWithLabel } from './Input'
import Modal, { ModalFooter, ModalProps } from './Modal'
import Radom, { Customer, DepositCryptoAddressType, PrivateTreasury } from '../state/Radom'
import { IS_LOCAL, RADOM_COLORS } from '../util/Constants'
import { DepositMethods, prodNetworks, testNetworks } from '../util/Managed'
import { errorToast, successToast, useTestMode } from '../util/Util'
import CustomerForm from './CustomerForm'
import SearchMultiselect from './SearchMultiselect'
import styled from 'styled-components'
import User from '../state/User'

interface Leaf { network: string, token?: string, name: string, logo: string, ticker?: string }

interface Selected { network: string, token: string | undefined }

interface ICreateDepositAddressModal extends ModalProps {
  onAccountCreated: () => void
}

const NATIVE = 'NATIVE'
const PRIVATE_TREASURY_NETWORKS = new Set([
  'Ethereum',
  'Polygon',
  'BNB',
  'Arbitrum',
  'Avalanche',
  'Base',
  'SepoliaTestnet'
])

export const AddButton = styled.div`
  width: 100%;
  padding: 14px;
  color: ${RADOM_COLORS.BLACK};
  cursor: pointer;

  :hover {
    background-color: ${RADOM_COLORS.GRAY_LIGHTEST};
  }
`
const CreateDepositAddressModal = observer(({
  title,
  onAccountCreated,
  onClose,
  visible
}: ICreateDepositAddressModal): ReactElement => {
  const [customers, setCustomers] = useState<Customer[]>([])
  const [customerFormVisible, setCustomerFormVisible] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [accountName, setAccountName] = useState('')
  const [selectedDepositMethods, setSelectedDepositMethods] =
    useState<Selected[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [testMode] = useTestMode()
  const [depositAddressType, setDepositCryptoAddressType] =
    useState<DepositCryptoAddressType>(DepositCryptoAddressType.FirstParty)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const [isPrivateTreasuryEnabled, setIsPrivateTreasuryEnabled] = useState(false)
  const [privateTreasury, setPrivateTreasury] = useState<PrivateTreasury | null>(null)
  const [selectedTreasuryId, setSelectedTreasuryId] = useState<string | undefined>(undefined)

  const [shouldHideMonero, setShouldHideMonero] = useState(true)

  useEffect(() => {
    User.getOrganization().then((o) => {
      setShouldHideMonero(!o.features?.isMoneroEnabled)
      const enabled = !!o.features?.isPrivateTreasuryEnabled
      setIsPrivateTreasuryEnabled(enabled)
      if (enabled) {
        Radom.getPrivateTreasury().then(setPrivateTreasury).catch(() => setPrivateTreasury(null))
      }
    })
  }, [])

  const loadCustomers = async (): Promise<void> => {
    Radom.listCustomers(0, 100, testMode ? testNetworks : prodNetworks)
      .then(c => {
        setCustomers(c.data)
      }).then(() => setCustomerFormVisible(false))
  }

  const createAddress = async (): Promise<void> => {
    if (selectedDepositMethods.length <= 0) {
      return
    }
    const isExternal = depositAddressType === DepositCryptoAddressType.ThirdParty
    const customerId = isExternal && selectedCustomer ? selectedCustomer.id : undefined
    setIsLoading(true)
    try {
      await Radom.createDepositAccount({
        name: accountName,
        accountDetails: {
          Crypto: {
            depositMethods: selectedDepositMethods
              .map(m => ({
                network: m.network,
                token: m.token
              })),
            isExternal,
            customerId,
            treasuryId: selectedTreasuryId
          }
        }
      })
      onAccountCreated()
      successToast('Deposit address created')
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  const leafKey = (x: Selected | Leaf): string => `${x.network}:${x.token ?? NATIVE}`

  const collectLeaves = (node: any): Leaf[] => {
    if (node?.method) {
      return [{
        network: node.method.network,
        token: node.method.token,
        name: node.name,
        logo: node.logo,
        ticker: node.ticker
      }]
    }
    if (node?.subMethods && node.subMethods.length > 0) {
      return node.subMethods.flatMap(collectLeaves)
    }
    if (node?.name) {
      return [{
        network: node.name,
        token: undefined,
        name: node.name,
        logo: node.logo,
        ticker: node.ticker
      }]
    }
    return []
  }

  const getCheckState = (leaves: Leaf[], selectedSet: Set<string>): { all: boolean, partial: boolean } => {
    const total = leaves.length
    const sel = leaves.reduce((a, l) => a + (selectedSet.has(leafKey(l as Selected)) ? 1 : 0), 0)
    const all = total > 0 && sel === total
    const none = sel === 0
    return { all, partial: !none && !all }
  }

  const selectGroupLeaves = (leaves: Leaf[]): Selected[] => leaves.map(l => ({
    network: l.network,
    token: l.token
  }))

  const hasGroupDetails = (group: any): boolean => Array.isArray(group?.subMethods) && group.subMethods.length > 0

  const renderBundledGroupDetails = (group: any): ReactElement => {
    const branches = group?.subMethods ?? []
    const hasNestedBranches = branches.some(
      (branch: any) => Array.isArray(branch?.subMethods) && branch.subMethods.length > 0
    )

    const rows = hasNestedBranches
      ? branches.map((branch: any) => ({
        label: branch.name,
        assets: (branch.subMethods ?? []).map((item: any) => item.ticker ?? item.name).join(', ')
      }))
      : [{
        label: 'Assets enabled',
        assets: branches.map((item: any) => item.ticker ?? item.name).join(', ')
      }]

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
        padding: '0 12px 12px 46px'
      }}>
        {rows.map((row) => (
          <div key={`${group.name}-${row.label}`} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ color: RADOM_COLORS.GRAY_DARKEST, fontSize: 12.5, fontWeight: 500 }}>{row.label}</span>
            <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 12.25, lineHeight: 1.45 }}>{row.assets}</span>
          </div>
        ))}
      </div>
    )
  }

  const selectedSet = useMemo(() => new Set(selectedDepositMethods.map(leafKey)), [selectedDepositMethods])
  const canUsePrivateTreasury = isPrivateTreasuryEnabled && privateTreasury?.status === 'active'
  const isPrivateTreasurySelected = !!selectedTreasuryId
  const shouldShowPrivateTreasuryOption = isPrivateTreasuryEnabled || !!privateTreasury
  const privateTreasuryOptionLabel = privateTreasury?.status === 'active'
      ? 'Private treasury'
      : privateTreasury?.status === 'pendingActivation'
        ? 'Private treasury · Pending activation'
        : privateTreasury?.status === 'activationFailed'
          ? 'Private treasury · Contact support'
          : 'Private treasury · Create on Balances'

  useEffect(() => {
    if (!canUsePrivateTreasury && selectedTreasuryId) {
      setSelectedTreasuryId(undefined)
    }
  }, [canUsePrivateTreasury, selectedTreasuryId])

  const supportedPrivateTreasuryNetworks = useMemo(() => {
    if (IS_LOCAL || testMode) {
      return new Set(['SepoliaTestnet'])
    }

    return PRIVATE_TREASURY_NETWORKS
  }, [testMode])

  useEffect(() => {
    if (isPrivateTreasurySelected) {
      setSelectedDepositMethods(prev => prev.filter(method => supportedPrivateTreasuryNetworks.has(method.network)))
    }
  }, [isPrivateTreasurySelected, supportedPrivateTreasuryNetworks])

  const topGroups = useMemo(
    () => DepositMethods
      .filter(d => (testMode ? d.isTestnet : !d.isTestnet))
      .filter((d) => {
        if (isPrivateTreasurySelected) {
          return (IS_LOCAL || testMode) ? d.name === 'SepoliaTestnet' : d.name === 'EVM'
        }

        if (d.name !== 'Monero' && d.name !== 'MoneroTestnet') {
          return true
        }
        return !shouldHideMonero
      }),
    [testMode, shouldHideMonero, isPrivateTreasurySelected]
  )
  const selectedGroup = useMemo(() => topGroups.find(group => {
    const leaves = collectLeaves(group)
    return leaves.length === selectedDepositMethods.length &&
      leaves.every(leaf => selectedSet.has(leafKey(leaf as Selected)))
  }), [topGroups, selectedDepositMethods, selectedSet])

  useEffect(() => {
    loadCustomers()
  }, [])

  return <Modal
    title={title}
    visible={visible}
    onClose={onClose}
    innerStyles={{ width: 550 }}>

    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'start',
      gap: 16
    }}>
      <div style={{
        padding: '14px 16px',
        borderRadius: 16,
        border: '1px solid #eceff3',
        background: '#f9fafb',
        color: RADOM_COLORS.GRAY_DARKEST,
        fontSize: 13.5,
        lineHeight: 1.55
      }}>
        Create a deposit address and choose whether it belongs to your business or to a specific customer.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: '14px 16px',
          borderRadius: 16,
          border: '1px solid #eceff3',
          background: '#fcfcfd'
        }}>
          <InputLabel>Who will own this address?</InputLabel>
          <div style={{ fontSize: 14, display: 'grid', gap: 10, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <SecondaryButton
              disabled={isLoading}
              onClick={() => {
                setDepositCryptoAddressType(DepositCryptoAddressType.FirstParty)
                if (selectedCustomer) {
                  setSelectedCustomer(null)
                }
              }} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: depositAddressType === DepositCryptoAddressType.FirstParty ? '1px solid #d7dbe3' : '1px solid #eceff3',
                padding: 12,
                minHeight: 88,
                justifyContent: 'flex-start',
                background: depositAddressType === DepositCryptoAddressType.FirstParty ? '#f7f7f8' : '#fff',
                color: depositAddressType ===
                  DepositCryptoAddressType.FirstParty
                  ? RADOM_COLORS.BLACK
                  : RADOM_COLORS.GRAY_DARKEST
              }}
              type='button'>
              <Checkbox checked={depositAddressType === DepositCryptoAddressType.FirstParty} />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>{DepositCryptoAddressType.FirstParty}</span>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>
                            Owned by your business
                </span>
              </div>
            </SecondaryButton>

            <SecondaryButton
              disabled={isLoading}
              onClick={() => {
                setDepositCryptoAddressType(DepositCryptoAddressType.ThirdParty)
                if (selectedCustomer) {
                  setSelectedCustomer(null)
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: depositAddressType === DepositCryptoAddressType.ThirdParty ? '1px solid #d7dbe3' : '1px solid #eceff3',
                padding: 12,
                minHeight: 88,
                justifyContent: 'flex-start',
                background: depositAddressType === DepositCryptoAddressType.ThirdParty ? '#f7f7f8' : '#fff',
                color: depositAddressType ===
                  DepositCryptoAddressType.ThirdParty
                  ? RADOM_COLORS.BLACK
                  : RADOM_COLORS.GRAY_DARKEST
              }}
              type='button'>
              <Checkbox checked={depositAddressType === DepositCryptoAddressType.ThirdParty} />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>{DepositCryptoAddressType.ThirdParty}</span>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>
                            Created for a customer
                </span>
              </div>
            </SecondaryButton>
          </div>
        </div>

        <TextInputWithLabel
          label='Address nickname'
          placeholder='Enter a name for this deposit address'
          disabled={isLoading}
          required
          value={accountName}
          onChange={(e) => {
            const el = (e.target as HTMLInputElement)
            const value = el.value
            setAccountName(value)
          }}
        />

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: '14px 16px',
          borderRadius: 16,
          border: '1px solid #eceff3',
          background: '#fcfcfd'
        }}>
          <InputLabel>Treasury</InputLabel>
          <div style={{
            display: 'grid',
            gap: 10,
            gridTemplateColumns: shouldShowPrivateTreasuryOption ? 'repeat(2, minmax(0, 1fr))' : '1fr'
          }}>
            <SecondaryButton
              disabled={isLoading}
              onClick={() => setSelectedTreasuryId(undefined)}
              style={{
                justifyContent: 'flex-start',
                minHeight: 64,
                background: !selectedTreasuryId ? '#f7f7f8' : '#fff',
                border: !selectedTreasuryId ? '1px solid #d7dbe3' : '1px solid #eceff3'
              }}
              type='button'>
              <Checkbox checked={!selectedTreasuryId} />
              <span>Radom treasury</span>
            </SecondaryButton>
            {shouldShowPrivateTreasuryOption && <SecondaryButton
              disabled={
                isLoading ||
                !canUsePrivateTreasury
              }
              onClick={() => canUsePrivateTreasury && setSelectedTreasuryId(privateTreasury.id)}
              style={{
                justifyContent: 'flex-start',
                minHeight: 64,
                background: selectedTreasuryId ? '#f7f7f8' : '#fff',
                border: selectedTreasuryId ? '1px solid #d7dbe3' : '1px solid #eceff3',
                opacity: canUsePrivateTreasury ? 1 : 0.55
              }}
              type='button'>
              <Checkbox checked={!!selectedTreasuryId} />
              <span>{privateTreasuryOptionLabel}</span>
            </SecondaryButton>}
          </div>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: '14px 16px',
          borderRadius: 16,
          border: '1px solid #eceff3',
          background: '#fcfcfd'
        }}>
          <InputLabel>Address type</InputLabel>
          <Dropdown
            innerStyle={{ maxHeight: 300 }}
            disabled={isLoading}
            selectedContent={
              selectedGroup
                ? <span style={{ fontSize: 14 }}>{selectedGroup.name}</span>
                : <span style={{ fontSize: 14 }}>Select address type</span>
            }
            dropdownContent={
              <DropdownContext.Consumer>
                {({ closeDropdown }) => <div onMouseDown={(e) => e.stopPropagation()}>
                  {topGroups.map((group, idx) => {
                    const groupLeaves = collectLeaves(group)
                    const groupState = getCheckState(groupLeaves, selectedSet)
                    const hasDetails = hasGroupDetails(group)
                    const isExpanded = !!expandedGroups[group.name]

                    return (
                      <div key={`g-${idx}`}>
                        <DropdownItem
                          style={{
                            background: groupState.all || groupState.partial ? '#f8fafc' : undefined
                          }}
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedDepositMethods(groupState.all ? [] : selectGroupLeaves(groupLeaves))
                            closeDropdown()
                          }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                              <div style={{
                                alignItems: 'center',
                                border: `1px solid ${groupState.all || groupState.partial ? RADOM_COLORS.BLACK : '#d8dde7'}`,
                                borderRadius: '50%',
                                display: 'flex',
                                flex: '0 0 auto',
                                height: 16,
                                justifyContent: 'center',
                                width: 16
                              }}>
                                {(groupState.all || groupState.partial) && <div style={{
                                  background: RADOM_COLORS.BLACK,
                                  borderRadius: '50%',
                                  height: 8,
                                  width: 8
                                }} />}
                              </div>
                              <div
                                style={{
                                  height: 25,
                                  width: 25,
                                  backgroundImage: `url(${group.logo})`,
                                  backgroundRepeat: 'no-repeat',
                                  backgroundSize: 'contain',
                                  backgroundPosition: 'center',
                                  position: 'relative'
                                }} />
                              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                <span style={{ fontSize: 14 }}>{group.name}</span>
                                {group.description &&
                                <span style={{ fontSize: 12, opacity: 0.5 }}>{group.description}</span>}
                              </div>
                            </div>
                            {hasDetails &&
                              <button
                                type='button'
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  setExpandedGroups((prev) => ({
                                    ...prev,
                                    [group.name]: !prev[group.name]
                                  }))
                                }}
                                style={{
                                  border: 'none',
                                  background: 'transparent',
                                  color: RADOM_COLORS.GRAY_DARKER,
                                  cursor: 'pointer',
                                  fontSize: 12.5,
                                  fontWeight: 500,
                                  padding: 0,
                                  flexShrink: 0
                                }}>
                                {isExpanded ? 'Hide details' : 'Show details'}
                              </button>}
                          </div>
                        </DropdownItem>
                        {hasDetails && isExpanded && renderBundledGroupDetails(group)}
                      </div>
                    )
                  })}
                </div>}
              </DropdownContext.Consumer>
            }
          />
        </div>
        { depositAddressType === DepositCryptoAddressType.ThirdParty &&
         <div style={{
           display: 'flex',
           flexDirection: 'column',
           gap: 10,
           padding: '14px 16px',
           borderRadius: 16,
           border: '1px solid #eceff3',
           background: '#fcfcfd'
         }}>
           <InputLabel>Customer</InputLabel>
           <SearchMultiselect
             isSingleSelect={true}
             isLoading={isLoading}
             placeholder="Select customer"
             dropdownPlaceholder="No customers found"
             itemArray={customers}
             selectedItems={selectedCustomer ? [selectedCustomer] : []}
             setSelectedItems={newItems => {
               setSelectedCustomer(newItems[0])
             }}
             headers={[
               <AddButton key="create_customer" onClick={() => setCustomerFormVisible(true)}>
                 <span>+ Create customer</span>
               </AddButton>
             ]}
             keyExtractor={(customer) => `${customer.id}${customer.name}${customer.email}`}
             labelExtractor={customer => customer.name ? `${customer.name} (${customer.email})` : customer.email}
             onNewItem={() => setCustomerFormVisible(true)}
           />
         </div>}
      </div>
    </div>

    <ModalFooter $fullWidthAction>
      <PrimaryButton
        isLoading={isLoading}
        disabled={!accountName || selectedDepositMethods.length <= 0 ||
           (depositAddressType === DepositCryptoAddressType.ThirdParty && !selectedCustomer)}
        style={{ width: '100%' }}
        onClick={() => {
          createAddress()
        }}>
          Create deposit address
      </PrimaryButton>
    </ModalFooter>

    <Modal
      visible={customerFormVisible}
      onClose={() => setCustomerFormVisible(false)}
      title='Create customer'>
      <div style={{ overflow: 'auto' }}>
        <CustomerForm onCreateCustomer={loadCustomers} />
      </div>
    </Modal>
  </Modal>
})

export default CreateDepositAddressModal
